import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import axios from "axios";
import { toast } from "sonner";
import { CreditCard, Check, Loader2, Gift, Sparkles } from "lucide-react";
import Sidebar from "@/components/Sidebar";
import { useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const GetCredits = () => {
  const navigate = useNavigate();
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasingPackage, setPurchasingPackage] = useState(null);
  const [packages, setPackages] = useState([]);
  const [activePromo, setActivePromo] = useState(null);

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/");
  };

  useEffect(() => {
    loadData();
    loadActivePromo();
    
    // Check for session_id in URL (returning from Stripe)
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId) {
      checkPaymentStatus(sessionId);
    }
  }, []);

  const loadActivePromo = async () => {
    try {
      const response = await axios.get(`${API}/promos/active`);
      if (response.data.has_promo) {
        setActivePromo(response.data);
      }
    } catch (error) {
      console.error("Failed to load promo:", error);
    }
  };

  const loadData = async () => {
    try {
      const [creditsRes, packagesRes] = await Promise.all([
        axios.get(`${API}/credits`),
        axios.get(`${API}/credit-packages`)
      ]);
      setCredits(creditsRes.data.credits);
      setPackages(packagesRes.data.packages || []);
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatus = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      
      if (response.data.status === 'complete' && response.data.payment_status === 'paid') {
        toast.success(response.data.message || 'Payment successful!');
        const creditsRes = await axios.get(`${API}/credits`);
        setCredits(creditsRes.data.credits);
        window.history.replaceState({}, '', '/credits');
      } else if (response.data.status === 'expired') {
        toast.error('Payment session expired. Please try again.');
        window.history.replaceState({}, '', '/credits');
      } else {
        setTimeout(() => checkPaymentStatus(sessionId), 3000);
      }
    } catch (error) {
      console.error('Payment status check failed:', error);
      toast.error('Failed to verify payment. Please contact support if you were charged.');
    }
  };

  const handlePurchase = async (packageId) => {
    setPurchasingPackage(packageId);
    
    try {
      const response = await axios.post(`${API}/checkout/create`, {
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.url) {
        const stripeWindow = window.open(response.data.url, '_blank');
        
        if (!stripeWindow) {
          toast.info("Opening payment page...");
          window.location.href = response.data.url;
        } else {
          toast.success("Payment window opened! Complete your purchase there.");
          setPurchasingPackage(null);
          
          const checkPayment = setInterval(async () => {
            try {
              const statusRes = await axios.get(`${API}/checkout/status/${response.data.session_id}`);
              if (statusRes.data.status === 'complete') {
                clearInterval(checkPayment);
                toast.success(statusRes.data.message);
                const creditsRes = await axios.get(`${API}/credits`);
                setCredits(creditsRes.data.credits);
              }
            } catch (e) {
              // Session might not exist yet
            }
          }, 3000);
          
          setTimeout(() => clearInterval(checkPayment), 600000);
        }
      } else {
        throw new Error('No checkout URL received');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || "Failed to start checkout");
      setPurchasingPackage(null);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-amber-600 mx-auto mb-4" />
          <p className="text-lg font-medium text-stone-700">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-stone-50 to-neutral-100 flex">
      <Sidebar onLogout={handleLogout} />
      <div className="flex-1 ml-0 md:ml-64 py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-stone-900 mb-4">
              Get Credits
            </h1>
            <p className="text-lg text-stone-600 max-w-2xl mx-auto">
              1 Credit = 1 Complete AI-Generated Book
            </p>
          </div>

          {/* PROMO BANNER */}
          {activePromo && activePromo.promo_type === "bogo" && (
            <Card className="mb-8 bg-gradient-to-r from-green-500 to-emerald-600 border-0 shadow-xl overflow-hidden">
              <CardContent className="py-6 px-8">
                <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center justify-center h-16 w-16 bg-white/20 rounded-full">
                      <Gift className="h-8 w-8 text-white" />
                    </div>
                    <div className="text-white text-center md:text-left">
                      <h2 className="text-2xl font-bold flex items-center gap-2">
                        <Sparkles className="h-6 w-6" />
                        BUY ONE GET ONE FREE!
                      </h2>
                      <p className="text-green-100 mt-1">
                        Purchase any credit package and get DOUBLE the credits!
                      </p>
                    </div>
                  </div>
                  <div className="bg-white/20 rounded-xl px-6 py-3 text-center">
                    <p className="text-white/80 text-sm font-medium uppercase tracking-wide">Offer Status</p>
                    {activePromo.end_date ? (
                      <>
                        <p className="text-white text-2xl font-bold">
                          {(() => {
                            const endDate = new Date(activePromo.end_date);
                            const now = new Date();
                            const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                            if (daysLeft <= 0) return "Last Day!";
                            if (daysLeft === 1) return "1 Day";
                            return `${daysLeft} Days`;
                          })()}
                        </p>
                        <p className="text-green-100 text-sm">remaining</p>
                      </>
                    ) : (
                      <>
                        <p className="text-white text-2xl font-bold">Active</p>
                        <p className="text-green-100 text-sm">Limited Time</p>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {activePromo && activePromo.promo_type === "percentage" && (
            <Card className="mb-8 bg-gradient-to-r from-purple-500 to-indigo-600 border-0 shadow-xl overflow-hidden">
              <CardContent className="py-6 px-8">
                <div className="flex flex-col md:flex-row items-center justify-center gap-4">
                  <div className="flex items-center justify-center h-16 w-16 bg-white/20 rounded-full">
                    <Sparkles className="h-8 w-8 text-white" />
                  </div>
                  <div className="text-white text-center">
                    <h2 className="text-2xl font-bold">
                      {activePromo.discount_percent}% OFF - {activePromo.name}!
                    </h2>
                    <p className="text-purple-100 mt-1">
                      Limited time discount on all credit packages
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <Card className="mb-12 bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
            <CardContent className="py-8">
              <div className="flex items-center justify-center gap-4">
                <div className="flex items-center justify-center h-16 w-16 bg-amber-500 rounded-full">
                  <CreditCard className="h-8 w-8 text-white" />
                </div>
                <div>
                  <p className="text-sm font-medium text-stone-600 uppercase tracking-wide mb-1">Available Credits</p>
                  <p className="text-5xl font-bold text-stone-900">{credits}</p>
                  <p className="text-sm text-stone-500 mt-1">
                    {credits === 0 && "Purchase credits below to start creating books"}
                    {credits === 1 && "You can create 1 book"}
                    {credits > 1 && `You can create ${credits} books`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {packages.map((pkg) => {
              const isPopular = pkg.id === '5_credits';
              const perCredit = (pkg.price / pkg.credits).toFixed(2);
              const basePrice = 57;
              const savings = pkg.credits > 1 
                ? Math.round(((basePrice * pkg.credits - pkg.price) / (basePrice * pkg.credits)) * 100)
                : 0;
              
              return (
                <Card 
                  key={pkg.id}
                  className={`relative ${isPopular ? 'border-amber-500 border-2 shadow-lg' : 'border-stone-200'}`}
                >
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                      <span className="bg-amber-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                        Best Value
                      </span>
                    </div>
                  )}
                  
                  <CardHeader>
                    <CardTitle className="text-2xl">{pkg.name}</CardTitle>
                    <CardDescription>
                      {pkg.credits === 1 ? 'Perfect for trying out' : `Create ${pkg.credits} books`}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent>
                    <div className="mb-6">
                      <div className="flex items-baseline gap-2 mb-2">
                        <span className="text-4xl font-bold text-stone-900">${pkg.price}</span>
                      </div>
                      <div className="text-stone-600">
                        {pkg.credits} {pkg.credits === 1 ? 'Credit' : 'Credits'}
                        <span className="text-sm ml-2 text-stone-500">(${perCredit}/credit)</span>
                      </div>
                      {savings > 0 && (
                        <div className="text-green-600 font-semibold text-sm mt-1">
                          Save {savings}%
                        </div>
                      )}
                      {/* BOGO Bonus Display */}
                      {activePromo && activePromo.promo_type === "bogo" && (
                        <div className="mt-3 p-2 bg-green-100 border border-green-300 rounded-lg">
                          <p className="text-green-800 font-bold text-center flex items-center justify-center gap-1">
                            <Gift className="h-4 w-4" />
                            Get {pkg.credits * 2} credits!
                          </p>
                          <p className="text-green-700 text-xs text-center">
                            ({pkg.credits} + {pkg.credits} FREE)
                          </p>
                        </div>
                      )}
                    </div>

                    <div className="space-y-3 mb-6">
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-stone-700">Full-length novels (40K-80K words)</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-stone-700">AI-powered writing</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-stone-700">Script conversion included</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <Check className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-sm text-stone-700">Unlimited edits</span>
                      </div>
                    </div>

                    <Button 
                      className="w-full"
                      variant={isPopular ? "default" : "outline"}
                      onClick={() => handlePurchase(pkg.id)}
                      disabled={purchasingPackage !== null}
                    >
                      {purchasingPackage === pkg.id ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Redirecting...
                        </>
                      ) : (
                        "Purchase"
                      )}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <Card className="bg-stone-50 border-stone-200">
            <CardContent className="py-6">
              <div className="flex items-center justify-center gap-3 text-stone-600">
                <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
                <span className="text-sm">
                  Secure payment powered by Stripe. Your payment information is never stored on our servers.
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GetCredits;
